import React, {useMemo, useState} from 'react';
import moment from 'moment';
import {
  DashboardLayout,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  SearchInput,
  TableLayout,
  SecondaryButton,
} from '../../components';
import {eye, lookupEmpty} from '../../assets/images/images';
import {useNavigate} from 'react-router-dom';
import {Constants} from '../../helpers';
import {useQuery} from 'react-query';
import {fetchAuthenticationCustomers} from '../../requests/queries/easyAuth';
import {useAllApps} from '../../hooks/useApps';
import {useAuthWidgetFlows} from '../../hooks/useWidgetFlows';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';

function AuthenticationCustomers() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const {appIds} = useAllApps();
  const appIDS = appIds?.join(',');
  const {getWidgetNameById} = useAuthWidgetFlows();

  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
  });

  const handleFilterChange = newValues => {
    setFilterValues(newValues);
    setQuery(prev => ({...prev, page: 1}));
  };

  const pageClick = selected => {
    setQuery({
      ...query,
      page: selected,
    });
  };

  const dateParams = filterValues.dateRange
    ? {
        start_date: moment(filterValues.dateRange.start).format('YYYY-MM-DD'),
        end_date: moment(filterValues.dateRange.end).format('YYYY-MM-DD'),
      }
    : {};

  const {data: authCustomersData, isLoading} = useQuery(
    [
      'auth-customers',
      {...query, app_ids: appIDS, term: searchTerm, ...dateParams},
    ],
    () =>
      fetchAuthenticationCustomers({
        ...query,
        app_ids: appIDS,
        term: searchTerm,
        ...dateParams,
      }),
    {
      enabled: appIds?.length > 0,
      retry: 1,
    },
  );

  const authCustomers = useMemo(
    () => !isLoading && authCustomersData?.data,
    [authCustomersData?.data, isLoading],
  );

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Customers</p>
      }
    >
      <div className="flex items-center py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SearchInput
          onChange={value => {
            setQuery({...query, page: 1});
            setSearchTerm(value);
          }}
        />

        <SecondaryButton buttonText="Filter" onClick={toggleFilterVisibility} />
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2 onFilterChange={handleFilterChange} filters={[]} />
      </div>
      <TableLayout negativeMargins className="w-full mt-6">
        <thead className="text-small font-semibold uppercase">
          <tr className="bg-white80">
            <th className="p-5 pl-12 font-medium text-left text-xs text-grey whitespace-nowrap">
              <span className="flex items-center">Name</span>
            </th>
            <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
              <span className="flex items-center">Flow Name</span>
            </th>
            <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
              <span className="flex items-center">User ID</span>
            </th>
            <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
              <span className="flex items-center">Reference id</span>
            </th>
            {/*<th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">*/}
            {/*  <span className="flex items-center">Email</span>*/}
            {/*</th>*/}

            <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
              date and time created
            </th>
            <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap"></th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td colSpan={8}>
              {isLoading ? (
                <Loader height={45} />
              ) : !isLoading && authCustomers?.length === 0 ? (
                <EmptyState
                  height={70}
                  body="View customer authentication analysis here"
                  src={lookupEmpty}
                  noBtn
                />
              ) : null}
            </td>
          </tr>
        </tbody>

        <tbody className="text-sm text-body bg-white">
          {!isLoading &&
            authCustomers?.map(data => (
              <tr
                onClick={() =>
                  data?.authentication_id &&
                  navigate(
                    Constants.PAGES_URL.AUTHENTICATIONS_BIO_DATA?.replace(
                      ':slug',
                      data?.authentication_id,
                    ),
                    {
                      state: {isRegistration: true},
                    },
                  )
                }
                className={`text-left border-b ${
                  data?.authentication_id && 'cursor-pointer'
                } border-grey60`}
                key={data?.reference_id}
              >
                <td className="p-4 pl-12 whitespace-nowrap">
                  {`${data?.first_name || 'N/A'} ${data?.last_name || ''}`}
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  {data?.flow_id ? getWidgetNameById(data?.flow_id) : 'N/A'}
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  {data?.customer_id || 'N/A'}
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  {data?.reference_id || '-'}
                </td>
                {/*<td className="p-4 pl-0 whitespace-nowrap">*/}
                {/*  {data?.email || 'N/A'}*/}
                {/*</td>*/}
                <td className="p-4 pl-0 whitespace-nowrap">
                  {moment(data?.datetime)?.format('DD MMM YYYY hh:mm A')}
                </td>
                {data?.authentication_id && (
                  <td className="p-4 pl-0 whitespace-nowrap">
                    <button className="h-4 aspect-square">
                      <img src={eye} alt="" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
        </tbody>
      </TableLayout>

      {!isLoading && authCustomers && authCustomers?.length !== 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
          <PageNumber
            currentPage={+query?.page}
            limit={query?.limit}
            totalCount={authCustomersData?.meta?.total_count}
          />

          <div className="flex items-center gap-5">
            <PageLimit
              onLimitChange={limit => {
                setQuery(prevState => ({
                  ...prevState,
                  limit,
                  page: 1,
                }));
              }}
              limit={query?.limit}
              total={authCustomersData?.meta?.total_count}
              length={authCustomers?.length}
            />

            <Pagination
              total={Math.ceil(
                authCustomersData?.meta?.total_count / query?.limit,
              )}
              current={Number(query?.page)}
              onPageChange={activePage => {
                pageClick(activePage);
                const searchParams = new URLSearchParams(
                  document.location.search,
                );
                searchParams.set('page', activePage);
                const newSearchParams = searchParams.toString();
                navigate(`?${newSearchParams}`);
              }}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default AuthenticationCustomers;
